/**
 * SyncStatusContext.tsx
 * 
 * Global sync status provider for LOCAL-FIRST offline architecture
 * Tracks sync state across the app and provides status for UI indicators
 * 
 * States:
 * - ONLINE: Connected and fully synced
 * - OFFLINE: No network connection
 * - SYNCING: Actively uploading/downloading data
 * 
 * INTEGRATED WITH MasterSync (January 2025):
 * - Unified sync across all modules (attendance, operations, connect)
 * - Module-aware sync based on user access
 * - Progress tracking with detailed stage information
 * 
 * @author TotalAccess Development Team
 * @date January 2025
 */

'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { networkState } from '@/lib/networkState';
import offlineStorage from '@/lib/offlineStorage';
import masterSync from '@/lib/masterSync';
import {
  getOperationsBreakdownFromMasterStats,
  type OperationSyncStepResult,
  type SyncProgress as MasterSyncProgress,
  type ModuleAccess,
} from '@/lib/masterSync';

import { isCapacitor } from '@/lib/platformUtils';
import { useAuth } from '@/hooks/useAuth';
import { useNavigation } from '@/hooks/useNavigation';
import offlineWorkItems from '@/lib/offlineWorkItems';
import {
  OPERATIONS_SYNC_ACTIVITY_EVENT,
  OPERATIONS_SYNC_PENDING_CHANGED_EVENT,
  type OperationsSyncActivityDetail,
} from '@/lib/operations/offlineSyncEvents';
import {
  getPendingChildEntryCount,
} from '@/lib/operations/smartTaskOfflineEntries';
import {
  getPendingSmartTaskActionCount,
} from '@/lib/operations/smartTaskOfflineActions';
import {
  getOfflineFormQueuePendingCount,
} from '@/hooks/useOfflineFormQueue';

// ==================== TYPES ====================

export type SyncStatus = 'online' | 'offline' | 'syncing';

export interface SyncProgress {
  stage: 'idle' | 'uploading' | 'downloading' | 'complete' | 'error' |
         'uploading-attendance' | 'uploading-messages' | 'uploading-emails' | 'uploading-workitems' |
         'downloading-attendance' | 'downloading-messages' | 'downloading-emails' | 'downloading-workitems' |
         'cleanup-pruning';
  message: string;
  uploaded?: number;
  downloaded?: number;
  total?: number;
  error?: string;
  // Extended progress from MasterSync
  currentModule?: 'attendance' | 'operations' | 'connect';
  attendanceProgress?: { uploaded: number; downloaded: number };
  messagesProgress?: { uploaded: number; downloaded: number };
  emailsProgress?: { downloaded: number };
  workItemsProgress?: { uploaded: number; downloaded: number };
}

export interface SyncStats {
  pendingActions: number;
  pendingWorkItems: number;
  pendingSmartTaskActions: number;
  cachedTemplates: number;
  cachedEmployees: number;
  pendingWorkItemUploads: number; // Photos, signatures, documents from SmartTask
  pendingChildEntries: number;
  pendingFormSubmissions: number;
  pendingMessages: number;       // Unsent messages
  pendingEmails: number;         // Unsent emails
  lastSyncTime: Date | null;
  lastSyncSuccess: boolean;
  lastOperationsSyncStepResults: OperationSyncStepResult[];
  // Module access info
  moduleAccess?: ModuleAccess;
}

interface SyncStatusContextValue {
  // Current status
  status: SyncStatus;
  progress: SyncProgress;
  stats: SyncStats;
  
  // Actions
  triggerSync: () => Promise<void>;
  refreshStats: () => Promise<void>;
  
  // Sync control
  startBackgroundSync: () => void;
  stopBackgroundSync: () => void;
  cancelSync: () => void;
  
  // Module access
  moduleAccess: ModuleAccess;
  totalPending: number;
}

interface NavigationItemLike {
  module_key?: string;
}

interface NavigationGroupLike {
  items?: NavigationItemLike[];
}

interface SyncStatusUserLike {
  id: number;
  clientId?: number;
  client_id?: number;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Sync failed';
}

// ==================== CONTEXT ====================

const SyncStatusContext = createContext<SyncStatusContextValue | null>(null);

// ==================== CONFIGURATION ====================

const CONFIG = {
  BACKGROUND_SYNC_INTERVAL_MS: 5 * 60 * 1000, // 5 minutes
  SYNC_DEBOUNCE_MS: 3000, // 3 seconds
  STATS_REFRESH_INTERVAL_MS: 30 * 1000, // 30 seconds
  OFFLINE_PREP_RETRY_MS: 15 * 60 * 1000, // 15 minutes between offline-prep retries
};

// ==================== PROVIDER ====================

export function SyncStatusProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const { navigationGroups } = useNavigation();
  
  const [status, setStatus] = useState<SyncStatus>('online');
  const [progress, setProgress] = useState<SyncProgress>({ stage: 'idle', message: '' });
  const [stats, setStats] = useState<SyncStats>({
    pendingActions: 0,
    pendingWorkItems: 0,
    pendingSmartTaskActions: 0,
    cachedTemplates: 0,
    cachedEmployees: 0,
    pendingWorkItemUploads: 0,
    pendingChildEntries: 0,
    pendingFormSubmissions: 0,
    pendingMessages: 0,
    pendingEmails: 0,
    lastSyncTime: null,
    lastSyncSuccess: true,
    lastOperationsSyncStepResults: [],
  });
  const [moduleAccess, setModuleAccess] = useState<ModuleAccess>({
    attendance: false,
    operations: false,
    connect: false
  });
  
  const isSyncingRef = useRef(false);
  const backgroundSyncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const statsRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const syncDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const operationsSyncResetTimerRef = useRef<NodeJS.Timeout | null>(null);
  const operationsSyncSourcesRef = useRef<Set<string>>(new Set());
  const configuredRef = useRef(false);

  // ========== CONFIGURE MASTER SYNC ==========
  
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !navigationGroups) {
      configuredRef.current = false;
      return;
    }
    
    // Detect module access from navigation groups
    const detectModuleAccess = (): ModuleAccess => {
      const access: ModuleAccess = {
        attendance: false,
        operations: false,
        connect: false
      };
      
      if (!navigationGroups) return access;
      
      Object.values(navigationGroups as Record<string, NavigationGroupLike>).forEach((group) => {
        if (!group.items) return;
        group.items.forEach((item) => {
          if (item.module_key === 'total-attendance') access.attendance = true;
          if (item.module_key === 'total-operations') access.operations = true;
          if (item.module_key === 'total-connect') access.connect = true;
        });
      });
      
      return access;
    };
    
    const access = detectModuleAccess();
    setModuleAccess((prev) => {
      if (
        prev.attendance === access.attendance &&
        prev.operations === access.operations &&
        prev.connect === access.connect
      ) {
        return prev;
      }
      return access;
    });
    
    // Get auth token
    const token = localStorage.getItem('token') || (() => {
      const authTokens = localStorage.getItem('auth_tokens');
      if (authTokens) return JSON.parse(authTokens).access_token;
      return null;
    })();
    
    if (token) {
      const syncUser = user as SyncStatusUserLike;
      masterSync.configure({
        userId: user.id,
        clientId: syncUser.clientId || syncUser.client_id || 1,
        authToken: token,
        moduleAccess: access
      });
      configuredRef.current = true;
      console.log('[SyncStatus] MasterSync configured with module access:', access);
    }
  }, [isAuthenticated, user, navigationGroups]);

  // ========== STATS REFRESH ==========
  
  const refreshStats = useCallback(async () => {
    try {
      // Get stats from MasterSync (unified source)
      const masterStats = await masterSync.getStats();
      const useMasterStats = configuredRef.current;
      
      // Also get legacy stats for backward compatibility
      let workItemPendingUploads = 0;
      let workItemPendingOperations = 0;
      try {
        const workItemStats = await offlineWorkItems.getStorageStats();
        workItemPendingOperations = workItemStats.pendingOperations || 0;
        workItemPendingUploads = workItemStats.pendingUploads || 0;
      } catch (_e) {
        // offlineWorkItems not available (SSR or not initialized)
      }
      
      const [attendanceStorageStats, pendingChildEntries, pendingFormSubmissions, pendingSmartTaskActions] = await Promise.all([
        offlineStorage.getStorageStats().catch(() => null),
        user?.client_id ? getPendingChildEntryCount(Number(user.client_id)).catch(() => 0) : Promise.resolve(0),
        getOfflineFormQueuePendingCount().catch(() => 0),
        user?.client_id && user?.id
          ? getPendingSmartTaskActionCount(Number(user.client_id), Number(user.id)).catch(() => 0)
          : Promise.resolve(0),
      ]);

      const masterOperationsBreakdown = getOperationsBreakdownFromMasterStats(masterStats);
      const resolvedPendingActions = useMasterStats
        ? masterStats.pendingAttendanceLogs || 0
        : attendanceStorageStats?.pending_actions || 0;
      const resolvedPendingWorkItems = useMasterStats
        ? masterOperationsBreakdown.pendingWorkItems
        : workItemPendingOperations;
      const resolvedPendingSmartTaskActions = useMasterStats
        ? masterOperationsBreakdown.pendingSmartTaskActions
        : pendingSmartTaskActions;
      const resolvedPendingWorkItemUploads = useMasterStats
        ? masterOperationsBreakdown.pendingWorkItemUploads
        : workItemPendingUploads;
      const resolvedPendingChildEntries = useMasterStats
        ? masterOperationsBreakdown.pendingChildEntries
        : pendingChildEntries;
      const resolvedPendingFormSubmissions = useMasterStats
        ? masterOperationsBreakdown.pendingFormSubmissions
        : pendingFormSubmissions;
      
      setStats({
        pendingActions: resolvedPendingActions,
        pendingWorkItems: resolvedPendingWorkItems,
        pendingSmartTaskActions: resolvedPendingSmartTaskActions,
        cachedTemplates: attendanceStorageStats?.templates || 0,
        cachedEmployees: attendanceStorageStats?.employees || 0,
        pendingWorkItemUploads: resolvedPendingWorkItemUploads,
        pendingChildEntries: resolvedPendingChildEntries,
        pendingFormSubmissions: resolvedPendingFormSubmissions,
        pendingMessages: useMasterStats ? masterStats.pendingMessages || 0 : 0,
        pendingEmails: useMasterStats ? masterStats.pendingEmails || 0 : 0,
        lastSyncTime: masterStats.lastFullSync || attendanceStorageStats?.last_sync || null,
        lastSyncSuccess: true,
        lastOperationsSyncStepResults: masterSync.getLastOperationsSyncStepResults(),
        moduleAccess
      });
    } catch (error) {
      console.error('[SyncStatus] Failed to refresh stats:', error);
      // Set default stats on error to prevent UI issues
      setStats(prev => ({ ...prev, lastSyncSuccess: false }));
    }
  }, [moduleAccess, user?.client_id, user?.id]);

  const resetToNetworkStatus = useCallback((delayMs = 0) => {
    if (operationsSyncResetTimerRef.current) {
      clearTimeout(operationsSyncResetTimerRef.current);
      operationsSyncResetTimerRef.current = null;
    }

    const applyReset = () => {
      if (isSyncingRef.current || operationsSyncSourcesRef.current.size > 0) {
        return;
      }
      setStatus(networkState.isOnline() ? 'online' : 'offline');
      setProgress({ stage: 'idle', message: '' });
    };

    if (delayMs > 0) {
      operationsSyncResetTimerRef.current = setTimeout(applyReset, delayMs);
      return;
    }

    applyReset();
  }, []);

  const handleOperationsSyncActivity = useCallback((detail: OperationsSyncActivityDetail) => {
    if (isSyncingRef.current) {
      return;
    }

    const sourceKey = detail.source;

    if (detail.phase === 'start' || detail.phase === 'progress') {
      if (operationsSyncResetTimerRef.current) {
        clearTimeout(operationsSyncResetTimerRef.current);
        operationsSyncResetTimerRef.current = null;
      }

      operationsSyncSourcesRef.current.add(sourceKey);
      setStatus('syncing');
      setProgress({
        stage: 'uploading-workitems',
        message: detail.message || 'Syncing operations offline data...',
        currentModule: 'operations',
      });
      return;
    }

    operationsSyncSourcesRef.current.delete(sourceKey);

    if (detail.phase === 'error') {
      setProgress({
        stage: 'error',
        message: detail.message || 'Operations sync failed',
        error: detail.error,
        currentModule: 'operations',
      });
      void refreshStats();
      resetToNetworkStatus(3000);
      return;
    }

    if (operationsSyncSourcesRef.current.size === 0) {
      setProgress({
        stage: 'complete',
        message: detail.message || 'Operations sync complete',
        currentModule: 'operations',
      });
      void refreshStats();
      resetToNetworkStatus(1500);
    }
  }, [refreshStats, resetToNetworkStatus]);

  // ========== SYNC LOGIC ==========
  
  const triggerSync = useCallback(async () => {
    if (isSyncingRef.current) {
      console.log('[SyncStatus] Sync already in progress');
      return;
    }
    
    const isOnline = networkState.isOnline();
    if (!isOnline) {
      console.log('[SyncStatus] Offline - cannot sync');
      setStatus('offline');
      return;
    }
    
    if (!configuredRef.current) {
      console.log('[SyncStatus] MasterSync not configured yet');
      return;
    }
    
    isSyncingRef.current = true;
    setStatus('syncing');
    setProgress({ stage: 'uploading', message: 'Starting unified sync...' });
    
    try {
      const result = await masterSync.triggerFullSync();
      
      console.log('[SyncStatus] MasterSync complete:', result);

      const attendanceResult = result.find((entry) => entry.module === 'attendance');
      const connectResult = result.find((entry) => entry.module === 'connect');
      const operationsResult = result.find((entry) => entry.module === 'operations');
      
      // Calculate totals
      const totalUploaded = 
        (attendanceResult?.uploaded || 0) + 
        (connectResult?.uploaded || 0) + 
        (operationsResult?.uploaded || 0);
      const totalDownloaded = 
        (attendanceResult?.downloaded || 0) + 
        (connectResult?.downloaded || 0) + 
        (operationsResult?.downloaded || 0);
      const operationsUploaded = operationsResult?.uploaded || 0;
      const operationsFailed = operationsResult?.failed || 0;
      
      setProgress({ 
        stage: 'complete', 
        message: `Synced ↑${totalUploaded} ↓${totalDownloaded} items`,
        uploaded: totalUploaded,
        downloaded: totalDownloaded
      });
      
      // Refresh stats with new data
      await refreshStats();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('work-item-sync-complete', {
          detail: {
            success: operationsUploaded,
            failed: operationsFailed,
          },
        }));
      }
      
      // Brief delay to show completion, then back to online
      setTimeout(() => {
        setStatus('online');
        setProgress({ stage: 'idle', message: '' });
      }, 1500);
      
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error('[SyncStatus] Sync failed:', error);
      setProgress({ 
        stage: 'error', 
        message: errorMessage,
        error: errorMessage 
      });
      
      setStats(prev => ({
        ...prev,
        lastSyncSuccess: false
      }));
      
      // Return to online status after error
      setTimeout(() => {
        setStatus(networkState.isOnline() ? 'online' : 'offline');
        setProgress({ stage: 'idle', message: '' });
      }, 3000);
      
    } finally {
      isSyncingRef.current = false;
    }
  }, [refreshStats]);
  
  // ========== CANCEL SYNC ==========
  
  const cancelSync = useCallback(() => {
    masterSync.cancelSync();
    isSyncingRef.current = false;
    operationsSyncSourcesRef.current.clear();
    setStatus(networkState.isOnline() ? 'online' : 'offline');
    setProgress({ stage: 'idle', message: 'Sync cancelled' });
  }, []);

  // ========== DEBOUNCED SYNC ==========
  
  const triggerDebouncedSync = useCallback(() => {
    if (syncDebounceTimerRef.current) {
      clearTimeout(syncDebounceTimerRef.current);
    }
    
    syncDebounceTimerRef.current = setTimeout(() => {
      triggerSync();
    }, CONFIG.SYNC_DEBOUNCE_MS);
  }, [triggerSync]);

  // ========== BACKGROUND SYNC ==========
  
  // Post-sync offline preparation: downloads the deep offline data
  // (attendance snapshot, operations storage) and caches attendance
  // assets (face-recognition models, pages) so SmartClock/SmartTask work
  // fully offline without the user ever opening the MasterSync modal.
  // Retried on each periodic sync until it succeeds once per session.
  const offlinePreparationDoneRef = useRef(false);
  const lastOfflinePrepAttemptAtRef = useRef(0);

  const runOfflinePreparationIfNeeded = useCallback(async () => {
    if (offlinePreparationDoneRef.current) return;
    if (!networkState.isOnline() || !configuredRef.current) return;
    if (!moduleAccess.attendance && !moduleAccess.operations) return;
    const now = Date.now();
    if (now - lastOfflinePrepAttemptAtRef.current < CONFIG.OFFLINE_PREP_RETRY_MS) return;
    lastOfflinePrepAttemptAtRef.current = now;

    try {
      console.log('[SyncStatus] Auto-preparing offline data', moduleAccess);
      const result = await masterSync.prepareOffline({
        attendanceMode: moduleAccess.attendance ? 'auto' : undefined,
      });
      if (result.success) {
        console.log('[SyncStatus] Offline preparation complete', {
          attendance: result.attendance,
          operations: result.operations,
        });
      } else {
        console.warn('[SyncStatus] Offline preparation completed with errors:', result.errors);
      }

      // On Capacitor with attendance access, also cache face recognition
      // models (MediaPipe/TF.js WASM + face-api models) so offline clock-in
      // works without having manually opened the MasterSync modal.
      if (isCapacitor() && moduleAccess.attendance && networkState.isOnline()) {
        try {
          const { prepareAttendanceOfflineAssets } = await import('@/lib/offline/prepareAttendanceOfflineAssets');
          const assetResult = await prepareAttendanceOfflineAssets({
            pageStepIndex: 0,
            modelStepIndex: 1,
            updateProgress: (step, index, status) => {
              console.log(`[SyncStatus] Attendance asset cache: ${step} [${index}] ${status}`);
            },
            logPrefix: '[SyncStatus]',
          });
          console.log('[SyncStatus] Attendance offline assets cached:', {
            modelsReady: assetResult.modelsReady,
            totalModelsCached: assetResult.totalModelsCached,
            error: assetResult.error,
          });
        } catch (assetError) {
          console.warn('[SyncStatus] Attendance offline asset caching failed:', assetError);
        }
      }

      // Only mark done when the core preparation succeeded — asset cache
      // failures alone still retry, they're non-fatal to clock-in.
      if (result.success) {
        offlinePreparationDoneRef.current = true;
      }
    } catch (error) {
      console.warn('[SyncStatus] Offline preparation failed:', error);
    }
  }, [moduleAccess]);

  const startBackgroundSync = useCallback((options?: { skipInitialSync?: boolean }) => {
    if (backgroundSyncIntervalRef.current) {
      return; // Already running
    }

    console.log('[SyncStatus] Starting background sync service');

    refreshStats();
    if (!options?.skipInitialSync && networkState.isOnline()) {
      triggerSync();
    }

    // Periodic sync — skipped while the app is backgrounded (WebView throttles
    // JS/network; the resume handler below catches up instead).
    backgroundSyncIntervalRef.current = setInterval(() => {
      if (!networkState.isOnline()) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void triggerSync().then(() => runOfflinePreparationIfNeeded());
    }, CONFIG.BACKGROUND_SYNC_INTERVAL_MS);

    // Periodic stats refresh
    statsRefreshIntervalRef.current = setInterval(() => {
      refreshStats();
    }, CONFIG.STATS_REFRESH_INTERVAL_MS);

  }, [triggerSync, refreshStats, runOfflinePreparationIfNeeded]);

  const stopBackgroundSync = useCallback(() => {
    if (backgroundSyncIntervalRef.current) {
      clearInterval(backgroundSyncIntervalRef.current);
      backgroundSyncIntervalRef.current = null;
    }
    if (statsRefreshIntervalRef.current) {
      clearInterval(statsRefreshIntervalRef.current);
      statsRefreshIntervalRef.current = null;
    }
    console.log('[SyncStatus] Background sync stopped');
  }, []);

  // ========== NETWORK STATE LISTENER ==========
  
  // Track if this is the initial mount - defer sync to avoid blocking cold start
  const isInitialMountRef = useRef(true);
  const initialSyncTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    // Initial network state
    setStatus(networkState.isOnline() ? 'online' : 'offline');

    // Subscribe to network changes
    const unsubscribe = networkState.subscribe((state) => {
      if (state.isOnline) {
        setStatus('online');
        // Only trigger immediate sync if NOT initial mount (user went offline/online)
        if (!isInitialMountRef.current) {
          console.log('[SyncStatus] Network online - triggering sync');
          triggerDebouncedSync();
        }
      } else {
        console.log('[SyncStatus] Network offline');
        setStatus('offline');
      }
    });

    // Periodic background sync — this is what keeps offline data fresh
    // without the user ever opening the MasterSync modal. skipInitialSync:
    // the deferred initial sync below owns run #1 (cold-start scheduling
    // plus the offline-preparation chain).
    startBackgroundSync({ skipInitialSync: true });

    // Catch up after resume: the periodic tick skips while backgrounded,
    // so trigger a debounced sync when the app returns to the foreground.
    const handleVisibilityResume = () => {
      if (document.visibilityState === 'visible' && networkState.isOnline()) {
        triggerDebouncedSync();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityResume);

    // COLD START OPTIMIZATION: Defer initial sync to after first paint
    // This allows the UI to render first, then sync in background.
    // Native: slightly longer delay so dashboard HTTP + IndexedDB are less contended with MasterSync.
    const initialSyncDelayMs = isCapacitor() ? 5500 : 3000;
    if (networkState.isOnline()) {
      initialSyncTimerRef.current = setTimeout(() => {
        if (isInitialMountRef.current) {
          console.log('[SyncStatus] Deferred initial sync (cold start optimization)');
          isInitialMountRef.current = false;
          void triggerSync().then(async () => {
            // After initial sync, auto-prepare offline data so modules work
            // without the user needing to navigate to them first. This ensures
            // SmartClock and SmartTask have cached data even if the user goes
            // offline before visiting those modules.
            await runOfflinePreparationIfNeeded();
          });
        }
      }, initialSyncDelayMs);
    } else {
      isInitialMountRef.current = false;
    }

    return () => {
      unsubscribe();
      stopBackgroundSync();
      document.removeEventListener('visibilitychange', handleVisibilityResume);
      if (syncDebounceTimerRef.current) {
        clearTimeout(syncDebounceTimerRef.current);
      }
      if (initialSyncTimerRef.current) {
        clearTimeout(initialSyncTimerRef.current);
      }
    };
  }, [triggerDebouncedSync, triggerSync, startBackgroundSync, stopBackgroundSync, runOfflinePreparationIfNeeded, moduleAccess]);

  useEffect(() => {
    const unsubscribe = masterSync.subscribe((syncProgress: MasterSyncProgress) => {
      if (syncProgress.stage === 'idle') {
        return;
      }

      setProgress((prev) => {
        const nextStage = syncProgress.stage as SyncProgress['stage'];
        const nextModule = syncProgress.module || undefined;
        if (
          prev.stage === nextStage &&
          prev.message === syncProgress.message &&
          prev.total === syncProgress.total &&
          prev.uploaded === syncProgress.current &&
          prev.currentModule === nextModule
        ) {
          return prev;
        }
        return {
          ...prev,
          stage: nextStage,
          message: syncProgress.message,
          total: syncProgress.total,
          uploaded: syncProgress.current,
          currentModule: nextModule,
        };
      });

      if (syncProgress.stage === 'error' || syncProgress.stage === 'complete') {
        setStatus(networkState.isOnline() ? 'online' : 'offline');
      } else {
        setStatus('syncing');
      }
    });

    return () => unsubscribe();
  }, []);

  // ========== INITIAL STATS LOAD ==========
  // COLD START OPTIMIZATION: Defer stats refresh to avoid blocking first render
  const initialStatsTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  useEffect(() => {
    const initialStatsDelayMs = isCapacitor() ? 4000 : 2000;
    initialStatsTimerRef.current = setTimeout(() => {
      refreshStats();
    }, initialStatsDelayMs);
    
    return () => {
      if (initialStatsTimerRef.current) {
        clearTimeout(initialStatsTimerRef.current);
      }
    };
  }, [refreshStats]);

  // ========== LISTEN FOR CLOCK ACTION EVENTS ==========
  
  useEffect(() => {
    const handleClockAction = () => {
      console.log('[SyncStatus] Clock action queued - refreshing stats');
      refreshStats();
      triggerDebouncedSync();
    };
    
    // Listen for work item upload events from SmartTask/useOfflineWorkItems
    const handleWorkItemUpload = () => {
      console.log('[SyncStatus] Work item upload queued - refreshing stats');
      refreshStats();
    };

    const handleOperationsSyncPendingChanged = () => {
      console.log('[SyncStatus] Operations pending counts changed - refreshing stats');
      refreshStats();
    };

    const handleOperationsSyncActivityEvent = (event: Event) => {
      const customEvent = event as CustomEvent<OperationsSyncActivityDetail>;
      if (!customEvent.detail) return;
      handleOperationsSyncActivity(customEvent.detail);
    };
    
    // Listen for custom event from localFirstAttendance
    window.addEventListener('clock-action-queued', handleClockAction);
    // Listen for custom event from offlineWorkItems
    window.addEventListener('work-item-upload-queued', handleWorkItemUpload);
    window.addEventListener('work-item-sync-complete', handleWorkItemUpload);
    window.addEventListener(OPERATIONS_SYNC_PENDING_CHANGED_EVENT, handleOperationsSyncPendingChanged);
    window.addEventListener(OPERATIONS_SYNC_ACTIVITY_EVENT, handleOperationsSyncActivityEvent);
    
    return () => {
      window.removeEventListener('clock-action-queued', handleClockAction);
      window.removeEventListener('work-item-upload-queued', handleWorkItemUpload);
      window.removeEventListener('work-item-sync-complete', handleWorkItemUpload);
      window.removeEventListener(OPERATIONS_SYNC_PENDING_CHANGED_EVENT, handleOperationsSyncPendingChanged);
      window.removeEventListener(OPERATIONS_SYNC_ACTIVITY_EVENT, handleOperationsSyncActivityEvent);
    };
  }, [handleOperationsSyncActivity, refreshStats, triggerDebouncedSync]);

  // Memoize context value to prevent unnecessary re-renders
  const totalPending = useMemo(() => 
    stats.pendingActions
      + stats.pendingWorkItems
      + stats.pendingSmartTaskActions
      + stats.pendingWorkItemUploads
      + stats.pendingChildEntries
      + stats.pendingFormSubmissions
      + stats.pendingMessages
      + stats.pendingEmails,
    [
      stats.pendingActions,
      stats.pendingWorkItems,
      stats.pendingSmartTaskActions,
      stats.pendingWorkItemUploads,
      stats.pendingChildEntries,
      stats.pendingFormSubmissions,
      stats.pendingMessages,
      stats.pendingEmails,
    ]
  );
  
  const value = useMemo<SyncStatusContextValue>(() => ({
    status,
    progress,
    stats,
    triggerSync,
    refreshStats,
    startBackgroundSync,
    stopBackgroundSync,
    cancelSync,
    moduleAccess,
    totalPending
  }), [status, progress, stats, triggerSync, refreshStats, startBackgroundSync, stopBackgroundSync, cancelSync, moduleAccess, totalPending]);

  return (
    <SyncStatusContext.Provider value={value}>
      {children}
    </SyncStatusContext.Provider>
  );
}

// ==================== HOOK ====================

export function useSyncStatus(): SyncStatusContextValue {
  const context = useContext(SyncStatusContext);
  
  if (!context) {
    // Return safe defaults if not in provider (SSR, etc.)
    return {
      status: 'online',
      progress: { stage: 'idle', message: '' },
      stats: {
        pendingActions: 0,
        pendingWorkItems: 0,
        pendingSmartTaskActions: 0,
        cachedTemplates: 0,
        cachedEmployees: 0,
        pendingWorkItemUploads: 0,
        pendingChildEntries: 0,
        pendingFormSubmissions: 0,
        pendingMessages: 0,
        pendingEmails: 0,
        lastSyncTime: null,
        lastSyncSuccess: true,
        lastOperationsSyncStepResults: [],
      },
      triggerSync: async () => {},
      refreshStats: async () => {},
      startBackgroundSync: () => {},
      stopBackgroundSync: () => {},
      cancelSync: () => {},
      moduleAccess: { attendance: false, operations: false, connect: false },
      totalPending: 0
    };
  }
  
  return context;
}

export default SyncStatusContext;
